/**
 * Learn → Class books: the PDFs the school gave the student's class.
 *
 * Read-only on purpose. A principal stocks the shelf from the school console,
 * per class; a student sees the books of the classes they are enrolled in now
 * and opens one in a new tab through a link that lasts five minutes. Leaving a
 * class takes its books off this page — the server re-reads the enrolment for
 * the list and again for every link.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { StudentBookDTO } from '@cwy/config/contracts';
import { apiErrorMessage, apiGet } from '@/lib/api';
import { formatBookSize, openBook } from '@/lib/books';
import { Card, StudentPage, StudentPageHeader } from '@/components/ds';

export function BooksPage() {
  const books = useQuery({
    queryKey: ['me', 'books'],
    queryFn: () => apiGet<StudentBookDTO[]>('/me/books'),
  });
  const [error, setError] = useState('');
  const [opening, setOpening] = useState<string | null>(null);

  // Grouped by class, in the order the classes first appear (newest book first).
  const byClass = new Map<string, { name: string; books: StudentBookDTO[] }>();
  for (const book of books.data ?? []) {
    const group = byClass.get(book.classroom.id) ?? { name: book.classroom.name, books: [] };
    group.books.push(book);
    byClass.set(book.classroom.id, group);
  }

  const open = (book: StudentBookDTO) => {
    setError('');
    setOpening(book.id);
    openBook(`/me/books/${book.id}/url`)
      .catch((err) => setError(apiErrorMessage(err)))
      .finally(() => setOpening(null));
  };

  return (
    <StudentPage className="gap-4">
      <StudentPageHeader
        title="Class books"
        subtitle="Books your school has shared with your class. Each opens in a new tab."
      />

      {error && <p className="text-sm text-danger">{error}</p>}

      {books.isPending && <p className="text-ink-soft">Loading…</p>}
      {books.error && (
        <Card>
          <p className="text-sm text-ink-soft">{apiErrorMessage(books.error)}</p>
        </Card>
      )}
      {books.data?.length === 0 && (
        <Card>
          <h3 className="text-base font-bold text-forest">No books yet</h3>
          <p className="mt-1 text-sm text-ink-soft">
            When your school adds a book for your class, it will be here.
          </p>
        </Card>
      )}

      {[...byClass.entries()].map(([classroomId, group]) => (
        <Card key={classroomId} title={group.name}>
          <ul className="flex flex-col gap-2">
            {group.books.map((book) => (
              <li key={book.id}>
                <button
                  type="button"
                  onClick={() => open(book)}
                  disabled={opening === book.id}
                  className="flex w-full items-center justify-between gap-3 rounded-[10px] border border-line px-3.5 py-2.5 text-left transition-colors hover:bg-slate-50"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-forest">
                      {book.title}
                    </span>
                    <span className="block text-xs text-ink-soft">
                      PDF · {formatBookSize(book.sizeBytes)} · added{' '}
                      {new Date(book.createdAt).toLocaleDateString('en-IN', {
                        dateStyle: 'medium',
                      })}
                    </span>
                  </span>
                  <span
                    className="shrink-0 text-xs font-semibold"
                    style={{ color: 'var(--text-accent)' }}
                  >
                    {opening === book.id ? 'Opening…' : 'Open'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      ))}
    </StudentPage>
  );
}
